// KIADÁSOK — a Pénzügy lap harmadik füle: a hónap bejövő számlái és költségei, besorolással.
//
// A fül fő MUNKÁJA a besorolás, ezért a lap teteje azt mondja ki, mennyi vár még rá, a kategória pedig
// HELYBEN szerkeszthető (lásd `ExpenseCategoryCell`). A hónap a héjból jön (a három fül ugyanarra a hónapra
// néz), a szűrők a közös `ListToolbar` popoverében élnek — mint a használati adatok lapon.
//
// A NAV-SOR és a KÉZI tétel ugyanabban a listában áll, de nem ugyanaz a jogosultságuk: a NAV-sor összege és
// szállítója az adóhatóság rekordja, tehát azon a szerkesztés/törlés akció MEG SEM JELENIK (a szerver
// ugyanezt 422-vel zárja — a felület csak őszinte tükör).

import { FINANCE_PERMISSIONS, hasAnyPermission } from '@atlas/shared';
import {
  ArchiveBoxXMarkIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  FolderArrowDownIcon,
  PencilSquareIcon,
  FolderOpenIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type TFunction } from 'i18next';
import { toast } from 'sonner';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BulkClassifyDialog } from '@/components/expenses/bulk-classify-dialog';
import { CategoryManagerSheet } from '@/components/expenses/category-manager-sheet';
import { ExpenseImportDialog } from '@/components/expenses/expense-import-dialog';
import { ExpenseMonthCell } from '@/components/expenses/expense-month-cell';
import { ExpenseCategoryCell } from '@/components/expenses/expense-category-cell';
import { ExpenseEmployeeCell } from '@/components/expenses/expense-employee-cell';
import { AmountFillDialog } from '@/components/expenses/amount-fill-dialog';
import { ExpenseFormSheet } from '@/components/expenses/expense-form-sheet';
import { NavSyncButton, useNavSync } from '@/components/expenses/nav-sync-button';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailsExpander, type DetailsRow } from '@/components/shared/details-expander';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { ListToolbar, type ToolbarFilterItem } from '@/components/shared/list-toolbar';
import { MonthStepper } from '@/components/shared/month-stepper';
import {
  DeleteRowAction,
  EditRowAction,
  RowActionButton,
  RowActions,
} from '@/components/shared/list-row-actions';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Text } from '@/components/ui/text';
import {
  categoryOptions,
  EXPENSE_ISSUERS,
  expenseKeys,
  UNCATEGORIZED,
} from '@/features/expenses/api';
import {
  ExpenseSummaryCards,
  type ExpenseSummaryToggle,
} from '@/features/expenses/components/expense-summary';
import { useDebouncedSearch } from '@/hooks/use-debounced-search';
import { useListFilters } from '@/hooks/use-list-filters';

import { api } from '@/lib/api';
import { formatDay } from '@/lib/format-datetime';
import { formatMoney } from '@/lib/format-money';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

import type { InfiniteData } from '@tanstack/react-query';

import type { FinanceView, FinanceViewProps } from '@/features/finance/view';
import { isForbiddenStatus } from '@/lib/error-status';
import type {
  ExpenseBulkClassifyBody,
  ExpenseCategoryName,
  ExpenseCategoryRow,
  ExpenseRow,
  ExpenseSort,
  ExpenseSummary,
} from '@atlas/contracts';

const ALL = 'all';
const PAGE_SIZE = 100;

/** A lista végtelen-lapozott cache-pillanatképe (az optimista írások rollback-alapja). */
type ListSnapshot = InfiniteData<{ status: number; body?: { rows: ExpenseRow[] } }>;

/** Egy sor eltávolítása a lapozott pillanatképből (elvetés/törlés optimista ága). */
function dropRow(old: ListSnapshot | undefined, id: string): ListSnapshot | undefined {
  if (!old) return old;
  return {
    ...old,
    pages: old.pages.map((page) =>
      page.body === undefined
        ? page
        : { ...page, body: { ...page.body, rows: page.body.rows.filter((r) => r.id !== id) } },
    ),
  };
}

/**
 * A fül szűrői az URL-ben (`exp_` prefix — a route `validateSearch`-e ismeri a kulcsokat). A `defaults`
 * egyben a kulcs-készlet, ezért MODUL-SZINTŰ konstans: stabil referencia, és nem épül újra renderenként.
 */
const FILTER_DEFAULTS = {
  exp_q: '',
  exp_category: ALL,
  exp_source: ALL,
  exp_issuer: ALL,
  exp_dismissed: 'hidden',
  /** EGY IMPORT-KÖTEG sorai — ide navigál az import jelentése („Megnézem a listán"). */
  exp_batch: '',
  /** A MÁSODIK teendő-szűrő: bér-típusú tételen ülő sor munkatárs nélkül. */
  exp_missing_employee: '',
  /** A HARMADIK teendő-szűrő: a még pótolatlan összegű sorok (az összegző számlálójából kapcsolható). */
  exp_missing_amount: '',
  // RENDEZÉS: az alap a legfrissebb KIÁLLÍTÁSÚ bizonylat elöl — KIÍRVA (nem üres default), mint a
  // számla-listán. Így a fejléc is JELÖLI, mi szerint áll a lista; a default-értékű kulcs nem kerül az URL-be.
  exp_sort: 'issueDate',
  exp_order: 'desc',
};

/** Egy soron FÜGGŐBEN lévő besorolás-módosítás. A kulcs HIÁNYA = „ehhez a mezőhöz nem nyúltak". */
interface PendingEdit {
  /** `null` = besorolatlan. */
  categoryId?: string | null;
  /** `null` = bizonylat szerinti (nincs kézi érték). */
  accountingMonth?: string | null;
  /** `null` = nincs személyhez kötve. */
  employeeIdentityId?: string | null;
}

/** A függőben szerkeszthető mezők. Mind `string | null` értékű, ezért egy közös olvasó elég. */
type PendingField = 'categoryId' | 'accountingMonth' | 'employeeIdentityId';

/** A sor MENTETT értéke egy mezőre — ehhez mérjük a függő választást (egyezésnél a kulcs kikerül). */
function savedValue(row: ExpenseRow, key: PendingField): string | null {
  if (key === 'categoryId') return row.categoryId;
  // `?? null` — a mező régebbi válaszokból (vagy teszt-fixture-ből) HIÁNYOZHAT; `undefined`-ot a választó
  // „van érték"-ként olvasna, és fölöslegesen kirajzolná a törlés-gombot egy üres cellára.
  if (key === 'employeeIdentityId') return row.employeeIdentityId ?? null;
  return row.accountingMonthManual ? row.accountingMonth : null;
}

/**
 * A kategória ŐS-LÁNCA (gyökértől a szülőig) és az ág GYÖKERE — az optimista sor ettől nem áll ág nélkül.
 * A `seen` halmaz a (hibás adatból eredő) kör-hivatkozás ellen véd.
 */
function ancestryOf(
  categoryId: string | null,
  categories: readonly ExpenseCategoryRow[],
): { path: ExpenseCategoryName[]; rootId: string | null } {
  const path: ExpenseCategoryName[] = [];
  if (categoryId === null) return { path, rootId: null };
  const seen = new Set<string>();
  let rootId: string | null = categoryId;
  let cursor = categories.find((c) => c.id === categoryId)?.parentId ?? null;
  while (cursor !== null && !seen.has(cursor)) {
    seen.add(cursor);
    const node = categories.find((c) => c.id === cursor);
    if (!node) break;
    path.unshift(node.name);
    rootId = node.id;
    cursor = node.parentId;
  }
  return { path, rootId };
}

export function useExpensesView({ enabled, month, onMonthChange }: FinanceViewProps): FinanceView {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  // a kiadás-kezelés / -törlés a közös pénzügyi jog-listából (`@atlas/shared` `FINANCE_PERMISSIONS`)
  const canManage = hasAnyPermission(FINANCE_PERMISSIONS.expensesManage, can);
  const canDelete = hasAnyPermission(FINANCE_PERMISSIONS.expensesDelete, can);

  // A NAV-lekérés vezérlője EGY példányban: a sáv-gomb ÉS a mobil-FAB ugyanazt az állapotot látja.
  const navSync = useNavSync(t);

  const { filters: urlFilters, setFilters } = useListFilters(FILTER_DEFAULTS);
  const q = urlFilters.exp_q;
  const categoryId = urlFilters.exp_category;
  const source = urlFilters.exp_source;
  const issuerKey = urlFilters.exp_issuer;
  const dismissedFilter = urlFilters.exp_dismissed as 'hidden' | 'only' | 'all';
  const importBatchId = urlFilters.exp_batch;
  const missingEmployee = urlFilters.exp_missing_employee === '1';
  const missingAmount = urlFilters.exp_missing_amount === '1';
  const sort = urlFilters.exp_sort;
  const order = urlFilters.exp_order === 'asc' ? 'asc' : 'desc';

  /**
   * Fejléc-kattintás: ÚJ oszlop → növekvő; UGYANAZ az oszlop → irány-váltás. A rendezés az URL-ben él, tehát
   * a rendezett nézet is linkelhető (és a „Továbbiak" ugyanazon a sorrenden lapoz tovább).
   */
  const toggleSort = useCallback(
    (col: string) =>
      setFilters(
        col === sort
          ? { exp_order: order === 'asc' ? 'desc' : 'asc' }
          : { exp_sort: col, exp_order: 'asc' },
      ),
    [setFilters, sort, order],
  );

  const setCategoryId = useCallback((v: string) => setFilters({ exp_category: v }), [setFilters]);
  const setSource = useCallback((v: string) => setFilters({ exp_source: v }), [setFilters]);
  const setIssuerKey = useCallback((v: string) => setFilters({ exp_issuer: v }), [setFilters]);
  const setDismissedFilter = useCallback(
    (v: 'hidden' | 'only' | 'all') => setFilters({ exp_dismissed: v }),
    [setFilters],
  );
  // A kereső a lekérdezés KULCSA → debounce, különben minden leütés ~4 Odin-olvasás (a közös hook).
  const commitSearch = useCallback((v: string) => setFilters({ exp_q: v }), [setFilters]);
  const { input: searchInput, setInput: setSearchInput } = useDebouncedSearch(q, commitSearch);
  const [formTarget, setFormTarget] = useState<ExpenseRow | null>(null);
  /** A MÁSOLÁS forrás-sora — az űrlap FELVÉTELKÉNT nyílik, ennek adataival előtöltve. */
  const [copyTarget, setCopyTarget] = useState<ExpenseRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  /** A hiányzó összeg pótlásának cél-sora (csak `amountUnknown` NAV-soron nyílik). */
  const [amountTarget, setAmountTarget] = useState<ExpenseRow | null>(null);
  const [amountOpen, setAmountOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExpenseRow | null>(null);
  // A KIJELÖLÉS lokális állapot, NEM URL: egy kijelölés nem nézet, hanem folyamatban lévő munka — linkelve
  // értelmetlen (a másik gépen más sorok lennének betöltve), és a visszalépés sem hozná vissza értelmesen.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const filters = useMemo(
    () => ({
      month,
      categoryId,
      issuerKey,
      source,
      dismissed: dismissedFilter,
      missingAmount,
      q,
      sort,
      order,
    }),
    [month, categoryId, issuerKey, source, dismissedFilter, missingAmount, q, sort, order],
  );
  const listKey = expenseKeys.list(filters);

  const categoriesQ = api.expenses.categories.useQuery(expenseKeys.categories(), undefined, {
    enabled,
    queryKey: expenseKeys.categories(),
  });
  const categories = useMemo(
    () => (categoriesQ.data?.status === 200 ? categoriesQ.data.body.rows : []),
    [categoriesQ.data],
  );

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled,
    queryFn: ({ pageParam }) =>
      api.expenses.list.query({
        query: {
          month,
          categoryId: categoryId === ALL ? undefined : categoryId,
          issuerKey: issuerKey === ALL ? undefined : issuerKey,
          source: source === ALL ? undefined : (source as 'nav' | 'manual'),
          dismissed: dismissedFilter,
          ...(importBatchId === '' ? {} : { importBatchId }),
          ...(missingEmployee ? { missingEmployee: true } : {}),
          ...(missingAmount ? { missingAmount: true } : {}),
          q: q === '' ? undefined : q,
          sort: sort === '' ? undefined : (sort as ExpenseSort),
          order: sort === '' ? undefined : order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(() => pages.flatMap((p) => (p.status === 200 ? p.body.rows : [])), [pages]);
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  // Az ÖSSZEGZÉS a hónap TELJES halmazán készül (a BFF a szűrés ELŐTT számolja) — a szűrt lista fölött is a
  // havi valóságot mutatja, mert a „mennyi ment el ebben a hónapban" kérdésre a szűrt összeg hazudna.
  const summary: ExpenseSummary | undefined =
    okPage?.status === 200 ? okPage.body.summary : undefined;

  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = infinite.isError || hasNoSuccess;
  const isForbidden = pages.some((p) => isForbiddenStatus(p.status));
  // az elvetés-szűrő alapja (rejtve) is szűkítés (Andi #95) — a „mind" az egyetlen szűretlen állapot
  const hasFilters =
    q !== '' ||
    categoryId !== ALL ||
    issuerKey !== ALL ||
    source !== ALL ||
    dismissedFilter !== 'all' ||
    missingEmployee ||
    missingAmount;

  /**
   * AZ ÖSSZEGZŐ KAPCSOLÓI. A három TEENDŐ (besorolatlan · munkatárs nélküli bér-tétel · összeg nélküli) a
   * számlálót a listára fordítja: a szám a hónap ÉLŐ (nem elvetett) soraira szól, ezért bekapcsoláskor az
   * elvetés-szűrő is az alapra áll — különben az „elvetettek" nézetből kattintva más halmaz jönne, mint amit a
   * szám mond. A teendők egymást KIZÁRJÁK (egy kattintás = egy kérdés); újabb kattintás ugyanarra leveszi.
   */
  const toggleSummaryFilter = useCallback(
    (which: ExpenseSummaryToggle) => {
      if (which === 'dismissed') {
        setFilters({ exp_dismissed: dismissedFilter === 'only' ? 'hidden' : 'only' });
        return;
      }
      const on =
        which === 'uncategorized'
          ? categoryId === UNCATEGORIZED
          : which === 'missingEmployee'
            ? missingEmployee
            : missingAmount;
      // mindhárom teendő-szűrő ALAPÁLLAPOTA — a választott ezt írja felül
      const cleared = { exp_category: ALL, exp_missing_employee: '', exp_missing_amount: '' };
      if (on) {
        setFilters(cleared);
        return;
      }
      setFilters({
        ...cleared,
        exp_dismissed: 'hidden',
        ...(which === 'uncategorized'
          ? { exp_category: UNCATEGORIZED }
          : which === 'missingEmployee'
            ? { exp_missing_employee: '1' }
            : { exp_missing_amount: '1' }),
      });
    },
    [setFilters, dismissedFilter, categoryId, missingEmployee, missingAmount],
  );

  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  /**
   * FÜGGŐBEN LÉVŐ (még nem mentett) besorolás-módosítás soronként.
   *
   * MIÉRT NEM MENT A VÁLASZTÓ MAGA (user-döntés): a kattintásra induló írás egy elvétett választást azonnal
   * kiküldött, és a visszavonás egy MÁSODIK írás volt — két verzió-lépés és két audit-bejegyzés a
   * bizonylaton. Mostantól a választás csak itt áll, és a sor végi „Mentés" küldi ki.
   *
   * MIÉRT A HOOKBAN, és nem a cellákban: a kategória és a vonatkozási hónap EGY kérésben megy ki (egy
   * verzió-lépés, egy audit-sor), tehát a két cella állapotát EGYÜTT kell látni. Lokális állapot, nem URL:
   * a félbehagyott szerkesztés nem nézet — linkelve értelmetlen, és a visszalépés sem hozná vissza.
   *
   * A mező HIÁNYA ≠ `null`: a `null` kifejezett érték (besorolatlan · bizonylat szerinti hónap), a hiányzó
   * kulcs azt jelenti, hogy ahhoz a mezőhöz nem nyúltak.
   */
  const [pending, setPending] = useState<ReadonlyMap<string, PendingEdit>>(new Map());

  /**
   * Egy mező függő értékének beállítása. Ha a választás VISSZAÁLL a sor jelenlegi értékére, a kulcs kikerül
   * — így a „Mentés" nem marad aktív olyan soron, amin végül nincs változás.
   */
  const setPendingField = useCallback(
    (row: ExpenseRow, key: PendingField, value: string | null): void => {
      setPending((prev) => {
        const next = new Map(prev);
        const edit = { ...(next.get(row.id) ?? {}) };
        if (value === savedValue(row, key)) delete edit[key];
        else edit[key] = value;
        if (Object.keys(edit).length === 0) next.delete(row.id);
        else next.set(row.id, edit);
        return next;
      });
    },
    [],
  );

  const clearPending = useCallback((id: string): void => {
    setPending((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  /**
   * A FÜGGŐ MÓDOSÍTÁS MENTÉSE — a kategória és a hónap EGY kérésben (a besorolás-úton, ami NAV-soron is
   * engedett). OPTIMISTA: a sor azonnal frissül, és ha MÁSIK hónapra került, ki is esik a nézett hónapból —
   * a lista a vonatkozási hónapon áll, tehát egy helyben frissített sor olyan tételt mutatna, ami már nem
   * ehhez a hónaphoz tartozik (és a lap összegében sem szerepel).
   */
  const saveMutation = useEntityMutation<{ row: ExpenseRow; edit: PendingEdit }, ListSnapshot>({
    mutationFn: ({ row, edit }) =>
      api.expenses.classify.mutation({
        params: { id: row.id },
        body: {
          categoryId: 'categoryId' in edit ? (edit.categoryId ?? null) : row.categoryId,
          note: row.note,
          // A HIÁNYZÓ mező jelentése a szerződésben „ne nyúlj hozzá" — ezért csak akkor küldjük, ha
          // tényleg állították. Így a besorolás mentése nem sorolja át csendben a hónapot, és fordítva.
          ...('accountingMonth' in edit ? { accountingMonth: edit.accountingMonth ?? null } : {}),
          ...('employeeIdentityId' in edit
            ? { employeeIdentityId: edit.employeeIdentityId ?? null }
            : {}),
        },
        headers: { 'if-match': String(row.version) },
      }),
    // A `all` prefix EGY sorban éri el a listát, az összegzést és a kategória-fát — egy írás mindhármat
    // elavulttá teszi (a darabszámok és a havi összeg is mozdul).
    invalidateKeys: [expenseKeys.all],
    optimistic: {
      queryKey: listKey,
      update: (old, vars) => {
        if (!old) return old;
        const { row, edit } = vars;
        const nextMonth =
          'accountingMonth' in edit ? (edit.accountingMonth ?? row.documentMonth) : undefined;
        if (nextMonth !== undefined && nextMonth !== month) return dropRow(old, row.id);
        const nextCategory = 'categoryId' in edit ? (edit.categoryId ?? null) : undefined;
        const picked =
          nextCategory === undefined || nextCategory === null
            ? undefined
            : categories.find((c) => c.id === nextCategory);
        const ancestry = nextCategory === undefined ? null : ancestryOf(nextCategory, categories);
        return {
          ...old,
          pages: old.pages.map((page) =>
            page.body === undefined
              ? page
              : {
                  ...page,
                  body: {
                    ...page.body,
                    rows: page.body.rows.map((r) =>
                      r.id === row.id
                        ? {
                            ...r,
                            ...(nextCategory === undefined
                              ? {}
                              : {
                                  categoryId: nextCategory,
                                  categoryName: picked?.name ?? null,
                                  categoryPath: ancestry?.path ?? [],
                                  rootCategoryId: ancestry?.rootId ?? null,
                                }),
                            ...(nextMonth === undefined
                              ? {}
                              : {
                                  accountingMonth: nextMonth,
                                  accountingMonthManual: edit.accountingMonth != null,
                                }),
                            ...('employeeIdentityId' in edit
                              ? {
                                  employeeIdentityId: edit.employeeIdentityId ?? null,
                                  // A NÉV a választóból jön (a sor csak az azonosítót kapja vissza az
                                  // optimista íráskor); a szerver válasza úgyis felülírja.
                                  employeeName:
                                    edit.employeeIdentityId == null ? null : r.employeeName,
                                }
                              : {}),
                          }
                        : r,
                    ),
                  },
                },
          ),
        };
      },
    },
    messages: {
      success: t('toast:expenses.classified'),
      conflict: t('toast:expenses.conflict'),
      forbidden: t('toast:expenses.forbidden'),
      validation: t('toast:expenses.validation'),
      error: t('toast:expenses.error'),
      unavailable: t('toast:expenses.unavailable'),
    },
  });

  /** A cellának átadott érték: a FÜGGŐ választás, ha van, különben a sor mentett értéke. */
  const valueOf = useCallback(
    (row: ExpenseRow, key: PendingField): string | null => {
      const edit = pending.get(row.id);
      return edit !== undefined && key in edit ? (edit[key] ?? null) : savedValue(row, key);
    },
    [pending],
  );

  /**
   * ÁLL-E a munkatárs-választó ezen a soron: a sor kategóriája BÉR TÍPUSÚ TÉTEL-e. A döntés a sor FÜGGŐ
   * kategóriáján áll, nem a mentetten: ha a kezelő épp most sorolta bér-tételbe a sort, a választó azonnal
   * használható legyen — különben egy köztes mentés kellene ahhoz, hogy a második döntést meg lehessen hozni.
   *
   * A MÁSODIK ág (már van érték) szándékos: egy kategória-váltás nem rejtheti el a megadott kötést.
   */
  const employeeAllowed = useCallback(
    (row: ExpenseRow): boolean => {
      if (valueOf(row, 'employeeIdentityId') !== null) return true;
      const id = valueOf(row, 'categoryId');
      return id !== null && categories.find((c) => c.id === id)?.requiresEmployee === true;
    },
    [valueOf, categories],
  );

  const saveRow = useCallback(
    (row: ExpenseRow): void => {
      const edit = pending.get(row.id);
      if (edit === undefined) return;
      clearPending(row.id);
      saveMutation.mutate({ row, edit });
    },
    [pending, clearPending, saveMutation],
  );

  // ELVETÉS / visszavétel. OPTIMISTA: a sor AZONNAL eltűnik (vagy visszatér) — a besorolás-menet közben
  // nem várunk a szerverre. A `dismissed` szűrő dönti el, melyik irány tünteti el: az alapértelmezett
  // `hidden` nézetből az elvetett sor kikerül, az `only` nézetből a visszavett.
  //
  // _(Korábban ez a komment optimistát ígért, a kód viszont csak az `onSettled` invalidálásra tüntette el
  // a sort — a sor másodpercekig ott maradt, és a következő sorra lépve ugrott meg alatta a lista.)_
  const dismissMutation = useEntityMutation<
    { id: string; version: number; dismissed: boolean },
    ListSnapshot
  >({
    mutationFn: ({ id, version, dismissed }) =>
      api.expenses.dismiss.mutation({
        params: { id },
        body: { dismissed },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [expenseKeys.all],
    optimistic: {
      queryKey: listKey,
      update: (old, vars) => (dismissedFilter === 'all' ? old : dropRow(old, vars.id)),
    },
    messages: {
      success: t('toast:expenses.dismissed'),
      conflict: t('toast:expenses.conflict'),
      forbidden: t('toast:expenses.forbidden'),
      validation: t('toast:expenses.validation'),
      error: t('toast:expenses.error'),
      unavailable: t('toast:expenses.unavailable'),
    },
  });

  /**
   * HIÁNYZÓ ÖSSZEG PÓTLÁSA. Optimistic frissítés NINCS: a szerver számolja/validálja a hármast, és a sor
   * `amountManual` jelölése is onnan származik — egy előre kitalált cache-állapot itt hazudhatna.
   */
  const amountMutation = useEntityMutation<
    { id: string; version: number; netAmount: string; vatAmount: string; grossAmount: string },
    ListSnapshot
  >({
    mutationFn: ({ id, version, netAmount, vatAmount, grossAmount }) =>
      api.expenses.amount.mutation({
        params: { id },
        body: { netAmount, vatAmount, grossAmount },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [expenseKeys.all],
    messages: {
      success: t('toast:expenses.amountSaved'),
      conflict: t('toast:expenses.conflict'),
      forbidden: t('toast:expenses.forbidden'),
      validation: t('toast:expenses.validation'),
      error: t('toast:expenses.error'),
      unavailable: t('toast:expenses.unavailable'),
    },
  });

  const deleteMutation = useEntityMutation<{ id: string }, ListSnapshot>({
    mutationFn: ({ id }) => api.expenses.remove.mutation({ params: { id } }),
    // A `all` prefix EGY sorban éri el a listát, az összegzést és a kategória-fát — egy írás
    // mindhármat elavulttá teszi (a darabszámok és a havi összeg is mozdul).
    invalidateKeys: [expenseKeys.all],
    // OPTIMISTA: a törölt sor azonnal eltűnik. Hibára a hook visszaállítja a pillanatképet.
    optimistic: { queryKey: listKey, update: (old, vars) => dropRow(old, vars.id) },
    messages: {
      success: t('toast:expenses.deleted'),
      conflict: t('toast:expenses.conflict'),
      forbidden: t('toast:expenses.forbidden'),
      // A törlés EGYETLEN 422-je a NAV-fal (a felületen el sem érhető — ez a kézzel összeállított kérés ága).
      validation: t('toast:expenses.navReadonly'),
      error: t('toast:expenses.error'),
      unavailable: t('toast:expenses.unavailable'),
    },
  });

  /**
   * TÖMEGES besorolás. A hívó CSAK id-kat és a patch-et adja — a sor-verziót SORONKÉNT a szerver olvassa
   * (a listának nincs friss verziója minden kijelölt sorra).
   *
   * NINCS optimista írás, és ez tudatos: egy köteg RÉSZLEGESEN is sikerülhet, tehát nem tudnánk előre, mit
   * rajzoljunk ki. A visszajelzés ezért a sor-szintű összegzésből jön, a lista pedig újratöltődik.
   */
  const bulkMutation = useMutation({
    mutationFn: (patch: Omit<ExpenseBulkClassifyBody, 'ids'>) =>
      api.expenses.bulkClassify.mutation({ body: { ...patch, ids: [...selectedIds] } }),
    onSuccess: (res) => {
      if (res.status !== 200) {
        toast.error(
          res.status === 403
            ? t('toast:expenses.forbidden')
            : res.status === 422
              ? t('toast:expenses.validation')
              : t('toast:expenses.error'),
        );
        return;
      }
      const { success, failed } = res.body.summary;
      if (failed === 0) toast.success(t('toast:expenses.bulkClassified', { count: success }));
      else if (success === 0) toast.error(t('toast:expenses.bulkFailed', { count: failed }));
      else toast.warning(t('toast:expenses.bulkPartial', { success, failed }));
      setBulkOpen(false);
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: expenseKeys.all });
    },
    onError: () => toast.error(t('toast:expenses.error')),
  });

  const columns: DataTableColumn<ExpenseRow>[] = [
    {
      // EGY OSZLOP: SZÁLLÍTÓ + a bizonylat többi azonosítója a „Részletek" alatt. Külön „Szállító" és
      // „Számlaszám" oszlop két, egyenként keskeny hasábot adott ugyanarról a DOLOGRÓL (melyik számla ez),
      // és a sor a többi lista-oldal nyelvétől is eltért: ott az első oszlop EGY anchor (`itemTitle`) +
      // a közös kibontó. A rendezés a SZÁLLÍTÓRA megy — az a sor olvasott azonosítója.
      id: 'supplier',
      header: t('expenses.columns.invoice'),
      sortable: true,
      sortKey: 'supplierName',
      card: 'title',
      width: 'minmax(0,2.4fr)',
      multiline: true,
      cell: (r) => <ExpenseInvoiceCell row={r} t={t} />,
      // MOBIL: a kibontó tartalma a kártya EGYETLEN „Részletek" rácsába megy (nem második kapcsolóként) —
      // ugyanaz a szerződés, mint az eszköz-listán.
      cardExtraItems: (r) => expenseDetailRows(r, t),
    },
    {
      id: 'category',
      header: t('expenses.columns.category'),
      sortable: true,
      sortKey: 'category',
      width: 'minmax(0,1.3fr)',
      cell: (r) => (
        <ExpenseCategoryCell
          row={r}
          categories={categories}
          value={valueOf(r, 'categoryId')}
          onChange={(next) => setPendingField(r, 'categoryId', next)}
          canManage={canManage}
        />
      ),
    },
    {
      // MUNKATÁRS — kihez tartozik a költség. A kategória MELLETT áll, mert ugyanaz a besorolás-munka, és a
      // három döntés EGY kérésben megy ki. KESKENY hasáb: a legtöbb soron üresen marad (a költségek
      // többsége nem köthető személyhez), tehát nem vehet el helyet a szállítótól és az összegtől.
      id: 'employee',
      header: t('expenses.employee'),
      sortable: false,
      width: 'minmax(0,1.1fr)',
      cell: (r) => (
        <ExpenseEmployeeCell
          row={r}
          value={valueOf(r, 'employeeIdentityId')}
          onChange={(next) => setPendingField(r, 'employeeIdentityId', next)}
          canManage={canManage}
          allowed={employeeAllowed(r)}
        />
      ),
    },
    {
      // VONATKOZÁSI HÓNAP — melyik hónap eredményét terheli a tétel. A kategória MELLETT áll, mert ugyanaz a
      // munka: a soron eldöntjük, hová tartozik (melyik költség-kategóriába ÉS melyik hónapba).
      id: 'accountingMonth',
      header: t('expenses.month.label'),
      sortable: true,
      sortKey: 'accountingMonth',
      width: 'minmax(0,0.9fr)',
      cell: (r) => (
        <ExpenseMonthCell
          row={r}
          value={valueOf(r, 'accountingMonth')}
          onChange={(next) => setPendingField(r, 'accountingMonth', next)}
          viewMonth={month}
          canManage={canManage}
        />
      ),
    },
    {
      // ÖSSZEG — a SZÁMLA-LISTA blokkja, 1:1: `címke — érték` párok kis betűvel, a számok a blokk jobb élén
      // egy vonalban, a bruttó előtt hajszálvonal. Ugyanaz az adat-hármas (nettó/áfa/bruttó), tehát ugyanaz
      // az alak — a korábbi „nagy nettó, alatta »bruttó …«" csak ezen az egy lapon létezett. Rendezés: nettó.
      id: 'amount',
      header: t('expenses.columns.amount'),
      sortable: true,
      sortKey: 'netAmount',
      width: 'minmax(0,1.6fr)',
      className: 'text-muted-foreground',
      multiline: true,
      cell: (r) => <ExpenseAmountCell row={r} locale={locale} t={t} />,
    },
    {
      // DÁTUMOK — a lista UTOLSÓ oszlopa, a számla-listával azonos alakban: `Kiállítva: …` és
      // `Esedékesség: …` címkével, lokalizált dátummal (a nyers `YYYY-MM-DD` csak ezen a lapon volt).
      // A rendezés a KIÁLLÍTÁSRA megy, és ez a lap ALAP-rendezése is (a legfrissebb bizonylat elöl).
      id: 'issueDate',
      header: t('expenses.columns.dates'),
      sortable: true,
      sortKey: 'issueDate',
      width: 'minmax(0,1.3fr)',
      className: 'text-muted-foreground',
      multiline: true,
      cell: (r) => (
        <div className="min-w-0 space-y-0.5">
          <div className="truncate">
            {t('expenses.issueDateLabel')}: {formatDay(r.issueDate, locale)}
          </div>
          {r.dueDate !== null && (
            <Text tone="tertiary" as="div" className="truncate">
              {t('expenses.dueDateLabel')}: {formatDay(r.dueDate, locale)}
            </Text>
          )}
        </div>
      ),
    },
  ];

  // A HÓNAP nem a popoverben van, hanem MINDIG LÁTHATÓAN a sáv elején (mindhárom pénzügy-fülön egyformán):
  // ez a lap fő dimenziója, a felhasználó léptetgeti — popover mögé rejtve a leggyakoribb művelet lenne a
  // legdrágább. Aktív szűrőnek továbbra sem számít.
  const filterItems: ToolbarFilterItem[] = [
    {
      kind: 'select',
      key: 'source',
      label: t('expenses.filters.source'),
      value: source,
      onChange: setSource,
      options: [
        { value: 'nav', label: t('expenses.source.nav') },
        { value: 'manual', label: t('expenses.source.manual') },
      ],
      allValue: ALL,
    },
    {
      // KIBOCSÁTÓ: melyik cégünk számláit nézzük. Nem „forrás" (az a NAV/kézi) — ez a MI oldalunk.
      kind: 'select',
      key: 'issuer',
      label: t('expenses.filters.issuer'),
      value: issuerKey,
      onChange: setIssuerKey,
      options: EXPENSE_ISSUERS.map((k) => ({
        value: k,
        label: t(`invoices.issuerLabel.${k}`, { defaultValue: k }),
      })),
      allValue: ALL,
    },
    {
      kind: 'select',
      key: 'category',
      label: t('expenses.filters.category'),
      value: categoryId,
      onChange: setCategoryId,
      // A SZINTET behúzás jelöli, nem írásjel a névben: a korábbi `· ` előtag a szűrő-gomb feliratába is
      // beleragadt, és három szinten amúgy sem tudta volna megkülönböztetni az alkategóriát a tételtől.
      options: [
        { value: UNCATEGORIZED, label: t('expenses.uncategorized') },
        ...categoryOptions(categories, locale),
      ],
      allValue: ALL,
    },
    {
      /**
       * STÁTUSZ — a sor ÉL-E, vagy el van vetve. UTOLSÓ a sorban: a többi szűrő azt mondja meg, MIT nézünk,
       * ez azt, hogy milyen ÁLLAPOTBAN — és alapértelmezésben nem is kell hozzányúlni.
       *
       * A „MIND" A LISTA TETEJÉN ÁLL, mint minden más szűrőnél (user-döntés); az alap (aktív, `hidden`) is
       * aktív szűrő — a badge jelzi, a „Szűrők törlése" a „mind"-re állít (Andi #95).
       *
       * A szerződés-értékek (`hidden`/`only`/`all`) változatlanok — csak a FELIRAT lett státusz-nyelvű, mert
       * a „Rejtve / Csak az elvetettek" a szűrő SAJÁT működését írta le, nem a soroktól kérdezett állapotot.
       */
      kind: 'select',
      key: 'status',
      label: t('expenses.filters.status'),
      value: dismissedFilter,
      onChange: (v: string) => setDismissedFilter(v as 'hidden' | 'only' | 'all'),
      allValue: 'all',
      options: [
        { value: 'hidden', label: t('expenses.dismissedFilter.hidden') },
        { value: 'only', label: t('expenses.dismissedFilter.only') },
      ],
    },
  ];

  const toolbar = (
    <ListToolbar
      leading={<MonthStepper month={month} onMonthChange={onMonthChange} />}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: t('common.searchPlaceholder'),
      }}
      filterItems={filterItems}
      meta={
        infinite.isFetching && !infinite.isFetchingNextPage
          ? t('expenses.loading')
          : !isError && !infinite.isLoading
            ? t('expenses.countOf', { shown: rows.length, total })
            : ''
      }
      actions={
        // A SÁVBAN CSAK AKCIÓ van, és MIND EGY MÉRETŰ — az „utolsó bizonylat" dátuma (állapot, nem akció) a
        // hónap jelvény-sorába került, az EXPORT pedig kikerült a lapról (nem kértük; négy egyforma súlyú
        // gomb zajos sávot adott).
        //
        // MOBILON a sáv-gombok ELREJTVE (a repo lista-mintája): 375 px-en a kereső + „Szűrők" az elsődleges,
        // a többi gomb mellettük tördelné a sávot. Az elsődleges akció a lap-héj FAB-jára kerül át
        // (`primaryAction`). KIVÉTEL a tömeges besorolás: az csak KIJELÖLÉSRE jelenik meg, tehát nem ül
        // állandóan a sávban, és a kijelölés maga is szándékos művelet volt — mobilon is ott a helye.
        // A DOBOZ MOBILON `display: contents` (MÉRT HIBA, 375 px-en Playwrighttal): flexként a doboz MAGA is
        // elem maradt — üresen (minden gyereke rejtve) is elvitte a sáv 8 px-es térközét, tehát a kereső +
        // „Szűrők" sor a jobb szél ELŐTT ért véget, míg a fölötte álló hónap-léptető a szélig futott (351 vs
        // 359 px). `contents` mellett a rejtett gyerekek nyomtalanok, a mobilon is látható „Besorolás (N)"
        // pedig a sáv közvetlen elemeként ül a helyén.
        <div className="contents md:flex md:items-center md:gap-3">
          <div className="hidden md:block">
            <NavSyncButton canManage={canManage} sync={navSync} t={t} />
          </div>
          <div className="hidden md:block">
            <Button variant="outline" onClick={() => setCategoriesOpen(true)}>
              <FolderOpenIcon className="size-4" />
              {t('expenses.categories.button')}
            </Button>
          </div>
          {/* TÖMEGES IMPORT — közvetlenül az „Új kiadás" ELŐTT (user-döntés): ugyanaz a művelet, csak sok
              sorra. A sáv keskeny, az előnézet viszont 14 oszlopos tábla — ezért a gomb itt áll, a felület
              pedig teljes szélességű modálban nyílik. */}
          {canManage && (
            <div className="hidden md:block">
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <ArrowUpTrayIcon className="size-4" />
                {t('expenses.import.button')}
              </Button>
            </div>
          )}
          {/* TÖMEGES BESOROLÁS — a gombok KÖZÖTT, közvetlenül az „Új kiadás" előtt: a kijelölésen végzett
              munka ugyanúgy akció, mint a másik kettő. Az ikon a „mappába sorolás" képe; a KATEGÓRIA-kezelő
              nyitott mappája szándékosan MÁS ikon — az a katalógust szerkeszti, ez besorol. */}
          {selectedIds.size > 0 && (
            <Button onClick={() => setBulkOpen(true)}>
              <FolderArrowDownIcon className="size-4" />
              {t('expenses.bulk.button', { count: selectedIds.size })}
            </Button>
          )}
          <div className="hidden md:block">
            <ListNewButton
              label={t('expenses.new')}
              enabled={canManage}
              disabledTooltip={t('expenses.newForbidden')}
              onClick={() => {
                setFormTarget(null);
                setCopyTarget(null);
                setFormOpen(true);
              }}
            />
          </div>
        </div>
      }
    />
  );

  const content = (
    <div className="flex h-full min-h-0 flex-col gap-3 px-4 pb-2">
      {/* Az összegző-csík CSAK akkor jelenik meg, ha van mit összegezni: üres hónapon a tábla üres-állapota
          mondja el ugyanazt, és a két egyforma mondat egymás alatt zaj lenne. */}
      {summary !== undefined && (summary.totals.length > 0 || summary.dismissed > 0) && (
        <ExpenseSummaryCards
          summary={summary}
          locale={locale}
          active={{
            uncategorized: categoryId === UNCATEGORIZED,
            missingEmployee,
            missingAmount,
            dismissed: dismissedFilter === 'only',
          }}
          onToggle={toggleSummaryFilter}
        />
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <DataTable<ExpenseRow>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          {...(sort === '' ? {} : { sort })}
          order={order}
          onSort={toggleSort}
          // KIJELÖLÉS csak írás-joggal: a köteges besorolás `finance.expenses.manage`-hez kötött (a szerver is).
          selection={
            canManage
              ? {
                  selectedIds,
                  onToggle: (id) =>
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    }),
                  onToggleAll: (ids, on) => setSelectedIds(on ? new Set(ids) : new Set()),
                  rowLabel: t('expenses.bulk.selectRow'),
                  allLabel: t('expenses.bulk.selectAll'),
                }
              : undefined
          }
          isLoading={infinite.isLoading}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('expenses.noMatches')}
                description={t('expenses.noMatchesDesc')}
              />
            ) : (
              <EmptyState title={t('expenses.empty')} description={t('expenses.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('common.actions')}
          rowActions={(row) =>
            // A NAV-soron NINCS szerkesztés és NINCS törlés: az adat az adóhatóság rekordja. Az ELVETÉS
            // viszont ott is megy — az a mi döntésünk a soron, nem az adóhatóság adata. A besorolás
            // ugyanígy (a kategória-cellában).
            canManage || canDelete ? (
              <RowActions>
                {canManage && row.source === 'manual' && row.dismissedAt === null && (
                  <EditRowAction
                    label={t('common.edit')}
                    onClick={() => {
                      setCopyTarget(null);
                      setFormTarget(row);
                      setFormOpen(true);
                    }}
                  />
                )}
                {/* MÁSOLÁS — a visszatérő kézi költség (bérleti díj, előfizetés) gyors felvitele: FELVÉTEL
                    a sor adataival, számlaszám nélkül. CSAK kézi soron: a NAV-sort a NAV hozza, a másolata
                    egy kézi duplikátum lenne. Elvetett soron is megy — a forrás állapota nem öröklődik. */}
                {canManage && row.source === 'manual' && (
                  <RowActionButton
                    label={t('expenses.copy')}
                    icon={DocumentDuplicateIcon}
                    onClick={() => {
                      setFormTarget(null);
                      setCopyTarget(row);
                      setFormOpen(true);
                    }}
                  />
                )}
                {canManage && row.amountUnknown && (
                  // A HARMADIK engedett írás a NAV-soron. A gomb CSAK ott jelenik meg, ahol a NAV tényleg
                  // nem közölt összeget — nem általános szerkesztés, és a szerver is pontosan ezt kapuzza.
                  // A PÓTLÁS UTÁN IS OTT MARAD: az `amountUnknown` a nyers digestből jön, azt a pótlás nem
                  // oltja ki (a bizonylat attól még egyszerűsített), tehát egy elgépelt összeg javítható.
                  <RowActionButton
                    label={row.amountManual ? t('expenses.amountEdit') : t('expenses.amountFill')}
                    icon={PencilSquareIcon}
                    onClick={() => {
                      setAmountTarget(row);
                      setAmountOpen(true);
                    }}
                  />
                )}
                {/* MENTÉS — a besorolás és a vonatkozási hónap függő módosítását EGY kérésben küldi ki.
                    CSAK akkor jelenik meg, ha tényleg van mit menteni: egy mindig ott álló, tétlen gomb
                    minden soron zaj lenne, és azt sugallná, hogy a sor szerkesztés alatt áll. A gomb az
                    ELVETÉS ELŐTT áll, mert a mentés a gyakoribb és az építő művelet. */}
                {canManage && pending.has(row.id) && (
                  <RowActionButton
                    label={t('expenses.saveRow')}
                    icon={CheckIcon}
                    onClick={() => saveRow(row)}
                  />
                )}
                {canManage && (
                  <RowActionButton
                    label={
                      row.dismissedAt === null ? t('expenses.dismiss') : t('expenses.undismiss')
                    }
                    icon={row.dismissedAt === null ? ArchiveBoxXMarkIcon : ArrowUturnLeftIcon}
                    onClick={() =>
                      dismissMutation.mutate({
                        id: row.id,
                        version: row.version,
                        dismissed: row.dismissedAt === null,
                      })
                    }
                  />
                )}
                {canDelete && row.source === 'manual' && (
                  <DeleteRowAction
                    label={t('common.delete')}
                    onClick={() => setDeleteTarget(row)}
                  />
                )}
              </RowActions>
            ) : null
          }
        />
      </div>

      <ExpenseFormSheet
        open={formOpen}
        onOpenChange={setFormOpen}
        expense={formTarget}
        copyFrom={copyTarget}
        categories={categories}
      />
      <AmountFillDialog
        open={amountOpen}
        onOpenChange={setAmountOpen}
        target={amountTarget}
        initial={
          amountTarget?.amountManual === true
            ? { netAmount: amountTarget.netAmount, vatAmount: amountTarget.vatAmount }
            : null
        }
        pending={amountMutation.isPending}
        onSubmit={(amounts) => {
          if (amountTarget === null) return;
          amountMutation.mutate(
            { id: amountTarget.id, version: amountTarget.version, ...amounts },
            { onSuccess: () => setAmountOpen(false) },
          );
        }}
      />
      <BulkClassifyDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        count={selectedIds.size}
        categories={categories}
        locale={locale}
        month={month}
        pending={bulkMutation.isPending}
        onSubmit={(patch) => bulkMutation.mutate(patch)}
      />
      <ExpenseImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        categories={categories}
        month={month}
        // A jelentésből EGY kattintással a köteg sorai: a lista a köteg-azonosítóra szűr, ÉS a köteg
        // hónapjára lép — a lista egy hónapot mutat, tehát a puszta szűrő üres listára vinne.
        onShowBatch={(batchId, batchMonth) => {
          setFilters({ exp_batch: batchId });
          if (batchMonth !== null && batchMonth !== month) onMonthChange(batchMonth);
        }}
      />

      <CategoryManagerSheet
        open={categoriesOpen}
        onOpenChange={setCategoriesOpen}
        categories={categories}
        canManage={canManage}
        canDelete={canDelete}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title={t('expenses.deleteTitle')}
        description={t('expenses.deleteDesc', { supplier: deleteTarget?.supplierName ?? '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        pending={deleteMutation.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(
            { id: deleteTarget.id },
            { onSettled: () => setDeleteTarget(null) },
          );
        }}
      />
    </div>
  );

  return {
    toolbar,
    content,
    // A mobil FAB elsődleges akciója — a sávban a gomb csak ≥md-n látszik (lásd az `actions` kommentjét).
    ...(canManage
      ? {
          primaryAction: {
            icon: <PlusIcon />,
            label: t('expenses.new'),
            onClick: () => {
              setFormTarget(null);
              setCopyTarget(null);
              setFormOpen(true);
            },
          },
        }
      : {}),
    // MOBIL: a sáv két másodlagos gombja (NAV-lekérés · kategória-kezelő) rejtve van — a FAB-ban viszont
    // elérhető marad, különben a lap mobilon kevesebbet tudna, mint deszktopon.
    secondaryActions: [
      ...(canManage && !navSync.disabled
        ? [
            {
              key: 'nav-sync',
              icon: <ArrowDownTrayIcon />,
              label: navSync.pending ? t('expenses.nav.running') : t('expenses.nav.action'),
              onClick: navSync.run,
            },
          ]
        : []),
      {
        key: 'categories',
        icon: <FolderOpenIcon />,
        label: t('expenses.categories.button'),
        onClick: () => setCategoriesOpen(true),
      },
    ],
  };
}

/**
 * A BIZONYLAT AZONOSÍTÓI a „Részletek" alatt: számlaszám · adószám · (módosító/sztornó esetén) az eredeti
 * számla · megjegyzés. Csak a KITÖLTÖTT mezők kerülnek be — üres címke:érték pár zajt ad, nem információt.
 *
 * MIÉRT EGY FÜGGVÉNY: ugyanezek a párok kellenek a DESKTOP kibontóba és a MOBIL-kártya „Részletek"
 * rácsába is (`cardExtraItems`) — két másolat garantáltan elcsúszna.
 */
function expenseDetailRows(row: ExpenseRow, t: TFunction): DetailsRow[] {
  const rows: DetailsRow[] = [
    {
      label: t('expenses.columns.invoiceNumber'),
      // A SOR MARAD akkor is, ha nincs bizonylat: a hiány itt INFORMÁCIÓ (nyugta, számla nélküli költség),
      // nem üres mező — a kihagyott sorból a kezelő azt olvasná, hogy elfelejtettük kiírni.
      value:
        row.invoiceNumber === null ? (
          <Text variant="secondary" as="span">
            {t('expenses.fields.noBillValue')}
          </Text>
        ) : (
          <span className="tabular-nums">{row.invoiceNumber}</span>
        ),
    },
  ];
  if (row.supplierTaxNumber !== null) {
    rows.push({
      label: t('expenses.columns.taxNumber'),
      value: <span className="tabular-nums">{row.supplierTaxNumber}</span>,
    });
  }
  // A MÓDOSÍTÓ/SZTORNÓ bizonylat önálló számla, az eredetire hivatkozva — a hivatkozás nélkül a sor
  // értelmezhetetlen (miért negatív, mihez tartozik). A BADGE maga kint marad a kibontó mellett; ide az
  // eredeti szám kerül, ami hosszú és a sorban úgysem olvasható el.
  if (row.originalInvoiceNumber !== null) {
    rows.push({
      label: t('expenses.columns.originalInvoiceNumber'),
      value: <span className="tabular-nums">{row.originalInvoiceNumber}</span>,
    });
  }
  // A MÁSIK IRÁNY: ezt a sort egy sztornó bizonylat semmisítette meg. A hivatkozás nélkül az elvetett sor
  // indoklás nélkül állna — a `STORNO` jelvény ugyanis a MÁSIK soron ül.
  // ⚠️ `!= null`, nem `!== null`: a mező a TELEPÍTÉSI ABLAKBAN hiányozhat a válaszból (amíg a BFF a korábbi
  // build), és `undefined !== null` MINDEN sorra igaz lenne — minden sor sztornózottnak látszana. Ugyanez a
  // csapda ütött már az „importált" jelölésen (mérve, lokálban).
  if (row.stornoedByInvoiceNumber != null) {
    rows.push({
      label: t('expenses.columns.stornoedBy'),
      value: <span className="tabular-nums">{row.stornoedByInvoiceNumber}</span>,
    });
  }
  if (row.note !== null) rows.push({ label: t('expenses.columns.note'), value: row.note });
  return rows;
}

/**
 * AZ „ÖSSZEG" OSZLOP cellája — a SZÁMLA-LISTA blokkja, azonos alakban: `címke — érték` párok kis betűvel, a
 * számok a blokk JOBB élén egy vonalban (`tabular-nums`), a bruttó előtt hajszálvonal. Ugyanaz az adat-hármas
 * áll itt is, tehát ugyanaz az alak — két külön vizuális nyelv ugyanarra a dologra csak a rendszert töri.
 *
 * KÉT KIVÉTEL marad a kiadás-oldalon (a NAV miatt, nem stílusból):
 *   • NINCS ÖSSZEG — az egyszerűsített NAV-számlához a digest nem közöl összeget, és a tárolt 0,00 a HIÁNY.
 *     Forintot írni ide azt jelentené, hogy a bizonylat ingyenes volt. Ezért a három szám helyett a hiányt
 *     mondjuk ki, figyelmeztető jellel.
 *   • KÉZZEL PÓTOLT összeg — valódi szám, de nem a NAV adata: ugyanaz a SÁRGA HÁROMSZÖG jelöli, ami a
 *     pótlás előtt a hiányt jelölte (a jelentés nem változott: „ez az összeg nem az adóhatóságtól jön").
 */
function ExpenseAmountCell({
  row,
  locale,
  t,
}: {
  row: ExpenseRow;
  locale: string;
  t: TFunction;
}): JSX.Element {
  if (row.amountUnknown && !row.amountManual) {
    // A felirat RÖVID (az oszlop keskeny, és a hosszú mondat levágódott — mérve élő adaton); a teljes
    // magyarázat a tooltipben van, hogy a jel önmagában is érthető maradjon.
    return (
      <span
        className="text-warning flex items-center gap-1"
        title={t('expenses.amountUnknownHint')}
      >
        <ExclamationTriangleIcon className="size-4 shrink-0" />
        <span className="truncate text-xs">{t('expenses.amountUnknown')}</span>
      </span>
    );
  }
  return (
    <div className="flex min-w-0 max-w-[13rem] flex-col gap-0.5 text-xs">
      <div className="flex justify-between gap-3">
        <span>{t('expenses.amountNet')}</span>
        <span
          className="text-foreground flex items-center gap-1 whitespace-nowrap font-medium tabular-nums"
          {...(row.amountManual
            ? {
                title: t('expenses.amountManualHint'),
                'aria-label': t('expenses.amountManualHint'),
              }
            : {})}
        >
          {row.amountManual && (
            <ExclamationTriangleIcon className="text-warning size-3.5 shrink-0" />
          )}
          {formatMoney(row.netAmount, row.currency, locale)}
        </span>
      </div>
      <div className="flex justify-between gap-3">
        <span>{t('expenses.amountVat')}</span>
        <span className="whitespace-nowrap tabular-nums">
          {formatMoney(row.vatAmount, row.currency, locale)}
        </span>
      </div>
      <Divider tone="hairline" inset={false} spacing="none" className="my-0.5" />
      <div className="flex justify-between gap-3">
        <span>{t('expenses.amountGrossShort')}</span>
        <span className="whitespace-nowrap tabular-nums">
          {formatMoney(row.grossAmount, row.currency, locale)}
        </span>
      </div>
    </div>
  );
}

/**
 * A „Számla" oszlop cellája: a SZÁLLÍTÓ mint sor-anchor (`itemTitle` — ugyanaz a betűméret/-vastagság, mint
 * a többi lista-oldal első oszlopában), alatta a KÖZÖS „Részletek" kibontó.
 *
 * A FORRÁS-badge a kibontó MELLETT áll (`trailing`), nem külön oszlopban: egy jelvény nem érdemel saját
 * hasábot, és így a sor egyetlen helyen mondja el, MELYIK bizonylatról van szó és HONNAN jött.
 */
function ExpenseInvoiceCell({ row, t }: { row: ExpenseRow; t: TFunction }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <Text variant="itemTitle" as="div" className="truncate">
        {row.supplierName}
      </Text>
      <DetailsExpander
        rows={expenseDetailRows(row, t)}
        trailing={
          <>
            <DomainBadge
              kind="expenseSource"
              value={row.source}
              label={t(`expenses.source.${row.source}`)}
            />
            {/* MELYIK CÉGÜNK költsége. A forrás-jelvény MELLETT: a kettő együtt mondja meg, honnan jött a
                sor és kinek a könyvelésébe tartozik — két külön adózó, két külön könyvelés. */}
            <DomainBadge
              kind="expenseIssuer"
              value={row.issuerKey}
              label={t(`invoices.issuerLabel.${row.issuerKey}`, { defaultValue: row.issuerKey })}
            />
            {/* IMPORTÁLT sor jele — a KÖTEG-azonosító jelenléte. Nem új `source` érték: a `source` ma azt is
                eldönti, hogy egy sor szerkeszthető-e, és egy harmadik érték minden ilyen ágat érintene.

                ⚠️ `!= null`, nem `!== null`: a mező RÉGEBBI válaszból hiányozhat (telepítés közben, amíg a
                BFF még a korábbi build), és az `undefined !== null` MINDEN sorra igaz lenne — a NAV-sorok is
                „importált" jelölést kaptak volna. Mérve, lokálban. */}
            {row.importBatchId != null && (
              <DomainBadge kind="marker" value="imported" label={t('expenses.import.rowBadge')} />
            )}
            {row.navOperation !== null && row.navOperation !== 'CREATE' && (
              <DomainBadge
                kind="navOperation"
                value={row.navOperation}
                label={t(`expenses.navOperation.${row.navOperation}`)}
              />
            )}
            {/* SZTORNÓZVA — a sztornó bizonylat CÉLPONTJA. Külön jelvény a `STORNO`-tól: ez a sor maga nem
                sztornó (a művelete `CREATE`), hanem az, amit semmissé tettek. A kettőt egy jelvénnyel
                jelölve a lista azt állítaná, hogy a számla sztornó bizonylat — az pedig a párja. */}
            {/* ⚠️ `!= null` (l. az „importált" jelölés indoklását fentebb): hiányzó mezőnél az
                `undefined !== null` MINDEN sorra sztornó-jelvényt rajzolna. */}
            {row.stornoedByInvoiceNumber != null && (
              <DomainBadge kind="expenseStorno" value="stornoed" label={t('expenses.stornoed')} />
            )}
          </>
        }
      />
    </div>
  );
}
